/**
 * `@whittlelabs/loom` — the consuming-side client.
 *
 * Exports:
 *  - `defineWorkflow` / `promptNode` / `httpNode` / `jobsCreateNode`: the
 *    fluent builder that produces a typed `WorkflowDefinition` value.
 *  - `principalJobPool` / `literalJobPool`: the substrate-
 *    shipped named pool resolvers. These are the *only* functions the
 *    builder accepts for a prompt node's `jobPool` field.
 *  - `seedWorkflows`: posts the canonical-form bundle to Loom's
 *    `POST /api/admin/seed-workflow` endpoint.
 *  - `canonicaliseWorkflow`: exposed for in-process testing.
 */

// Strategy runtime (also available via the `@whittlelabs/loom/runtime` subpath
// for bundlers; re-exported here so classic-moduleResolution consumers can reach it).
export * from './runtime';
export { buildPromptExecutionInputs } from './authoring/prompt-execution';
export type { EnqueuePromptExecutionOptions } from './authoring/prompt-execution';

export { defineWorkflow } from './builder/define-workflow';
export { promptNode, httpNode, jobsCreateNode } from './builder/nodes';
export {
  principalJobPool,
  literalJobPool,
  describeJobPoolRef,
} from './builder/resolvers';
export { synthesiseLinearChain } from './builder/transitions';
export { canonicaliseWorkflow } from './seed/canonicalise';
export { seedWorkflows } from './seed/seed-workflows';
export type { SeedWorkflowsOptions, SeedWorkflowsResult } from './seed/seed-workflows';
export { WorkflowBuilderError, SeedWorkflowsError } from './errors';
export type {
  JobPoolRef,
  EntryBehavior,
  EmitOutput,
  HttpNodeOptions,
  JobsCreateNodeOptions,
  CanonicalNodeBody,
  CanonicalSeedRequest,
  NodeBehaviorConfig,
  PromptBehaviorConfig,
  HttpRequestBehaviorConfig,
  JobsCreateBehaviorConfig,
  Edge,
  NodeDef,
  PromptNodeOptions,
  PromptRendering,
  QualityGate,
  SerialisedJobPoolRef,
  SessionContext,
  TransitionResolver,
  WorkflowDefinition,
} from './types';
